import React, { useState, useEffect } from 'react';
import {
  Save,
  X,
  ShieldCheck,
  AlertTriangle,
  Info,
  Layers
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import PreContractService from '../../services/preContractService';

const CreatePolicy = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const { success, error: showError, toasts, removeToast } = useToast();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Value Threshold',
    enforcement: 'Critical Block',
    thresholdValue: '',
    currency: 'USD',
    isActive: true,
    appliesTo: 'All Contract Types'
  });

  // Fetch existing policy (Edit mode)
  useEffect(() => {
    const fetchPolicy = async () => {
      if (!isEditMode) return;

      setIsLoading(true);
      try {
        const response = await PreContractService.getPolicy(id);

        if (response?.data) {
          const policy = response.data;

          setFormData({
            name: policy.name || '',
            description: policy.description || '',
            type: policy.type || 'Value Threshold',
            enforcement: policy.enforcement || 'Critical Block',
            thresholdValue: policy.thresholdValue || '',
            currency: policy.currency || 'USD',
            isActive: policy.isActive ?? true,
            appliesTo: policy.appliesTo || 'All Contract Types'
          });
        }
      } catch (err) {
        showError('Failed to load policy details.');
        navigate('/contracts/pre-contract/policies');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicy();
  }, [id, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.thresholdValue) {
      showError('Please fill in all required fields.');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode) {
        await PreContractService.updatePolicy(id, formData);
        success('Compliance policy updated successfully');
      } else {
        await PreContractService.createPolicy(formData);
        success('Compliance policy created successfully');
      }

      setTimeout(() => {
        navigate('/contracts/pre-contract/policies');
      }, 800);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save policy.');
    } finally {
      setIsSaving(false);
    }
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: () => navigate('/contracts/pre-contract/policies'),
      variant: 'outline'
    },
    {
      label: isSaving
        ? 'Saving...'
        : isEditMode
          ? 'Update Policy'
          : 'Save Policy',
      icon: <Save size={18} />,
      onClick: handleSave,
      variant: 'primary',
      disabled: isSaving || isLoading
    }
  ];

  if (isLoading) {
    return (
      <div className="p-5 text-center">
        Loading policy details...
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={isEditMode ? 'Edit Policy' : 'Create New Policy'}
        description={
          isEditMode
            ? 'Modify existing compliance rules.'
            : 'Define a new compliance gate to validate contracts before execution.'
        }
        actions={headerActions}
      />

      <div className="page-container">
        {/* Row 1: Main Form */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <form onSubmit={handleSave}>
                  <div className="row g-4">

                    <div className="col-12">
                      <label className="form-label fw-bold">
                        Policy Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder="e.g., High Value Procurement Gate"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-bold">Description</label>
                      <textarea
                        name="description"
                        className="form-control"
                        rows="3"
                        placeholder="Describe the purpose and logic of this policy..."
                        value={formData.description}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Rule Type</label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <Layers size={16} />
                        </span>
                        <select
                          className="form-select"
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                        >
                          <option value="Value Threshold">Value Threshold</option>
                          <option value="Template Check">Template Check</option>
                          <option value="Jurisdiction">Jurisdiction / Region</option>
                          <option value="Vendor Risk">Vendor Risk Score</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Enforcement Level
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          {formData.enforcement === 'Critical Block' ? (
                            <ShieldCheck size={16} className="text-danger" />
                          ) : (
                            <AlertTriangle size={16} className="text-warning" />
                          )}
                        </span>
                        <select
                          className="form-select"
                          name="enforcement"
                          value={formData.enforcement}
                          onChange={handleChange}
                        >
                          <option value="Critical Block">
                            Critical Block
                          </option>
                          <option value="Warning">
                            Warning
                          </option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-8">
                      <label className="form-label fw-bold">
                        Threshold / Condition <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="thresholdValue"
                        className="form-control"
                        placeholder={
                          formData.type === 'Value Threshold'
                            ? 'e.g., 100000'
                            : 'Define rule condition'
                        }
                        value={formData.thresholdValue}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-bold">Status</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleChange}
                          id="policyStatus"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="policyStatus"
                        >
                          {formData.isActive ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>

                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Helper and Preview side-by-side */}
        <div className="row g-4">
          <div className="col-lg-6 mb-4">
            <div className="card bg-light border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h6 className="fw-bold d-flex align-items-center gap-2 mb-3">
                  <Info size={18} className="text-primary" />
                  Policy Logic Helper
                </h6>
                <div style={{ lineHeight: '1.6' }}>
                  <p className="small text-secondary mb-3">
                    <strong className="text-dark">Critical Blocks:</strong> Contracts that fail this rule
                    cannot be sent for signature until modified.
                  </p>
                  <p className="small text-secondary mb-0">
                    <strong className="text-dark">Warnings:</strong> Users will see a compliance flag
                    but can proceed with justification.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4 pb-6">
            <div className="card border-0 shadow-sm border-start border-primary border-4 h-100">
              <div className="card-body p-4">
                <div className="small text-uppercase fw-bold text-muted mb-3" style={{ letterSpacing: '0.05em' }}>
                  Live Preview
                </div>
                <div className="p-3 border rounded bg-white shadow-sm">
                  <div className="fw-bold text-primary mb-2" style={{ fontSize: '15px' }}>
                    {formData.name || 'Untitled Policy'}
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <span className="badge bg-light text-dark border px-2 py-1" style={{ fontWeight: '500' }}>
                      {formData.type}
                    </span>
                    <span
                      className={`small fw-bold ${formData.enforcement === 'Critical Block'
                        ? 'text-danger'
                        : 'text-warning'
                        }`}
                    >
                      ● {formData.enforcement}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreatePolicy;
